import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import ThreeHero, { NodeModal } from "@/components/ThreeHero";
import { Reveal, StaggerGroup, StaggerItem } from "@/components/Reveal";
import CountUp from "@/components/CountUp";
import Tilt from "@/components/Tilt";
import useMagnetic from "@/components/useMagnetic";
import { useLang } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiError } from "@/lib/api";
import {
  ArrowUpRight, ArrowRight, Building2, Search, Bot, Network, Check,
  Zap, Shield, Globe2, Sparkles, Quote, Plus, Minus,
} from "lucide-react";

const ICONS = { company: Building2, seo: Search, scraper: Network, agent: Bot };

export default function Landing() {
  const { t, lang } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [contact, setContact] = useState({ name: "", email: "", company: "", message: "" });
  const [contactLoading, setContactLoading] = useState(false);
  const [payLoading, setPayLoading] = useState(null);
  const [openFaq, setOpenFaq] = useState(0);
  const [modalKey, setModalKey] = useState(null);

  const heroPrimaryRef = useMagnetic({ strength: 0.25, radius: 100 });
  const heroSecondaryRef = useMagnetic({ strength: 0.2, radius: 100 });

  const { scrollYProgress } = useScroll();
  const blobY = useTransform(scrollYProgress, [0, 1], [0, -300]);
  const blob2Y = useTransform(scrollYProgress, [0, 1], [0, 200]);

  const handleCheckout = async (planId) => {
    if (!user) { navigate(`/register?plan=${planId}`); return; }
    try {
      setPayLoading(planId);
      const { data } = await api.post("/payments/checkout", { plan_id: planId, origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) { toast.error(formatApiError(e)); setPayLoading(null); }
  };

  const handleContact = async (e) => {
    e.preventDefault();
    setContactLoading(true);
    try {
      await api.post("/contact", contact);
      toast.success(t.contact.success);
      setContact({ name: "", email: "", company: "", message: "" });
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setContactLoading(false); }
  };

  const tiers = [
    { id: "starter", price: 29, ...t.pricing.tiers.starter },
    { id: "pro", price: 99, featured: true, ...t.pricing.tiers.pro },
    { id: "enterprise", price: 299, ...t.pricing.tiers.enterprise },
  ];

  const features = t.features.map((f) => ({
    ...f,
    Icon: { zap: Zap, shield: Shield, globe: Globe2, spark: Sparkles }[f.icon],
  }));

  const testimonials = t.testimonials.items;

  const faqs = t.faq.items;

  return (
    <div className="min-h-screen flex flex-col relative overflow-x-hidden" data-testid="landing-page">
      {/* Floating gradient blobs */}
      <motion.div style={{ y: blobY }} className="aq-blob top-[-10%] left-[-15%]" aria-hidden="true">
        <div className="w-full h-full" style={{ background: "radial-gradient(circle, #EAE4D5, transparent 60%)" }} />
      </motion.div>
      <motion.div style={{ y: blob2Y }} className="aq-blob top-[40%] right-[-20%]" aria-hidden="true">
        <div className="w-full h-full" style={{ background: "radial-gradient(circle, #B6B09F, transparent 60%)" }} />
      </motion.div>

      <Header />

      {/* HERO */}
      <section className="relative aq-grain pt-8 md:pt-16 pb-24 md:pb-36" data-testid="hero-section">
        <div className="relative max-w-7xl mx-auto px-5 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
              className="lg:col-span-7"
            >
              <div className="label-eyebrow mb-6 flex items-center gap-3">
                <span className="w-2 h-2 rounded-full bg-[#1E3A5F] animate-pulse" /> {t.hero.eyebrow}
              </div>
              <h1 className="font-serif-display text-[2.6rem] sm:text-[4rem] lg:text-[5.6rem] leading-[0.95] tracking-tighter">
                {t.hero.title_a}{" "}
                <em className="text-[#1E3A5F] not-italic">{t.hero.title_b}</em>{" "}
                {t.hero.title_c}
              </h1>
              <p className="mt-8 max-w-xl text-base md:text-lg text-[#4A4A4A] leading-relaxed">{t.hero.subtitle}</p>
              <div className="mt-10 flex flex-wrap gap-4">
                <button ref={heroPrimaryRef} onClick={() => navigate(user ? "/dashboard" : "/register")} className="aq-button-primary" data-testid="hero-cta-primary">
                  {t.hero.cta_primary} <ArrowUpRight size={16} />
                </button>
                <a ref={heroSecondaryRef} href="#services" className="aq-button-ghost" data-testid="hero-cta-secondary">
                  {t.hero.cta_secondary} <ArrowRight size={16} />
                </a>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1.2, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
              className="lg:col-span-5 relative h-[420px] md:h-[520px]"
            >
              <ThreeHero
                className="absolute inset-0"
                onNodeClick={(key) => setModalKey(key)}
                nodes={[
                  { key: "services", label: t.nav.services },
                  { key: "pricing", label: t.nav.pricing },
                  { key: "about", label: t.nav.about },
                  { key: "contact", label: t.nav.contact },
                  { key: "auth", label: user ? t.nav.dashboard : t.nav.register },
                ]}
              />
            </motion.div>
          </div>
        </div>

        {/* Marquee */}
        <div className="mt-16 overflow-hidden border-y border-[#B6B09F]/40 bg-[#EAE4D5]/40 backdrop-blur-sm">
          <div className="aq-marquee py-4 font-mono-aq text-[0.72rem] uppercase tracking-[0.3em] text-[#0A0A0A]/80">
            {Array.from({ length: 2 }).map((_, i) => (
              <div key={i} className="flex gap-16 shrink-0">
                {t.marquee.map((m, j) => (
                  <span key={`${i}-${j}`} className="flex items-center gap-16">
                    {m}<span>·</span>
                  </span>
                ))}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section id="services" className="py-24 md:py-36 relative" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <Reveal className="mb-16 max-w-3xl">
            <div className="label-eyebrow mb-4">— {t.services.eyebrow}</div>
            <h2 className="font-serif-display text-4xl md:text-6xl leading-[1.02] tracking-tighter">{t.services.title}</h2>
            <p className="mt-6 text-[#4A4A4A] text-lg leading-relaxed">{t.services.desc}</p>
          </Reveal>

          <StaggerGroup className="grid grid-cols-1 md:grid-cols-12 gap-5">
            {t.services.items.map((s, i) => {
              const Icon = ICONS[s.key];
              const span = i === 0 ? "md:col-span-7" : i === 1 ? "md:col-span-5" : i === 2 ? "md:col-span-5" : "md:col-span-7";
              return (
                <StaggerItem key={s.key} className={span}>
                  <Tilt max={5} className="h-full">
                    <article
                      onClick={() => navigate(user ? `/dashboard/services/${s.key}` : `/register`)}
                      className="aq-card p-8 md:p-10 relative overflow-hidden group h-full cursor-pointer"
                      data-testid={`service-card-${s.key}`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="w-12 h-12 rounded-full border border-[#B6B09F] flex items-center justify-center group-hover:border-[#1E3A5F] group-hover:text-[#1E3A5F] group-hover:scale-110 transition-all duration-300">
                          <Icon size={20} />
                        </div>
                        <span className="font-mono-aq text-[0.7rem] tracking-[0.2em] text-[#B6B09F]">0{i + 1}</span>
                      </div>
                      <h3 className="font-serif-display text-3xl md:text-4xl mt-8 leading-tight">{s.title}</h3>
                      <p className="mt-4 text-[#4A4A4A] leading-relaxed text-[0.97rem] max-w-md">{s.desc}</p>
                      <div className="mt-8 flex items-center gap-2 font-mono-aq text-[0.7rem] uppercase tracking-[0.2em] text-[#0A0A0A] group-hover:text-[#1E3A5F] transition-colors">
                        <span>{t.services.details}</span><ArrowUpRight size={14} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                      </div>
                    </article>
                  </Tilt>
                </StaggerItem>
              );
            })}
          </StaggerGroup>

          {/* Feature row */}
          <StaggerGroup className="mt-20 grid grid-cols-1 md:grid-cols-4 gap-5">
            {features.map((f) => (
              <StaggerItem key={f.title}>
                <div className="aq-card p-6 h-full">
                  <f.Icon size={20} className="text-[#1E3A5F]" />
                  <div className="font-serif-display text-2xl mt-4">{f.title}</div>
                  <div className="text-sm text-[#4A4A4A] mt-2 leading-relaxed">{f.desc}</div>
                </div>
              </StaggerItem>
            ))}
          </StaggerGroup>
        </div>
      </section>

      {/* ABOUT / STATS */}
      <section id="about" className="py-24 md:py-36 relative" data-testid="about-section">
        <div className="max-w-7xl mx-auto px-5 md:px-8 grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
          <Reveal className="md:col-span-5">
            <div className="aspect-[4/5] w-full overflow-hidden rounded-2xl relative bg-[#0A0A0A]" data-testid="about-watermark">
              {/* Radial spotlight for 3D volumetric depth */}
              <div className="absolute inset-0" style={{
                background: "radial-gradient(circle at 50% 45%, rgba(30,58,95,0.35) 0%, rgba(10,10,10,0) 55%)"
              }} />
              {/* Faint grid overlay for architectural feel */}
              <div className="absolute inset-0 opacity-[0.08]" style={{
                backgroundImage: "linear-gradient(#EAE4D5 1px, transparent 1px), linear-gradient(90deg, #EAE4D5 1px, transparent 1px)",
                backgroundSize: "48px 48px",
                maskImage: "radial-gradient(circle at 50% 45%, black 20%, transparent 70%)"
              }} />
              {/* Layered logo — outermost = wide soft shadow (aura), then blurred backdrop, then crisp foreground */}
              <img src="https://customer-assets.emergentagent.com/job_55d8f0a9-10a3-4632-9c2f-47ab4244182d/artifacts/ganp80c6_logo_symbol.png"
                alt="" aria-hidden="true"
                className="absolute left-1/2 top-1/2 aq-logo-blend"
                style={{
                  width: "78%",
                  transform: "translate(-50%, -50%) scale(1.06)",
                  filter: "blur(30px)",
                  opacity: 0.55,
                }} />
              <img src="https://customer-assets.emergentagent.com/job_55d8f0a9-10a3-4632-9c2f-47ab4244182d/artifacts/ganp80c6_logo_symbol.png"
                alt="" aria-hidden="true"
                className="absolute left-1/2 top-1/2 aq-logo-blend"
                style={{
                  width: "58%",
                  transform: "translate(-50%, -50%)",
                  opacity: 0.85,
                  filter: "drop-shadow(0 24px 60px rgba(30,58,95,0.45))",
                  animation: "aq-float 7s ease-in-out infinite",
                }} />
              {/* Highlight shine sweep on hover */}
              <div className="absolute inset-0 pointer-events-none opacity-0 hover:opacity-100 transition-opacity duration-700" style={{
                background: "linear-gradient(135deg, transparent 30%, rgba(234,228,213,0.06) 50%, transparent 70%)"
              }} />
              {/* Watermark label */}
              <div className="absolute bottom-6 left-6 font-mono-aq text-[0.62rem] tracking-[0.3em] uppercase text-[#B6B09F]/70">
                AQ · Intelligence Engine
              </div>
            </div>
          </Reveal>
          <div className="md:col-span-7 md:pl-8">
            <Reveal>
              <div className="label-eyebrow mb-4">— {t.about.eyebrow}</div>
              <h2 className="font-serif-display text-4xl md:text-6xl leading-[1.02] tracking-tighter">{t.about.title}</h2>
              <p className="mt-6 text-[#4A4A4A] text-lg leading-relaxed max-w-xl">{t.about.desc}</p>
            </Reveal>
            <StaggerGroup className="mt-12 grid grid-cols-2 gap-y-10 gap-x-6">
              {t.about.stats.map((st, i) => (
                <StaggerItem key={i}>
                  <div data-testid={`stat-${i}`}>
                    <div className="font-serif-display text-5xl md:text-6xl text-[#0A0A0A] tracking-tighter">
                      <CountUp value={st.v} />
                    </div>
                    <div className="mt-2 font-mono-aq text-[0.7rem] tracking-[0.2em] uppercase text-[#0A0A0A]/60">{st.l}</div>
                  </div>
                </StaggerItem>
              ))}
            </StaggerGroup>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-24 md:py-32" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <Reveal>
            <div className="label-eyebrow mb-4">— {t.testimonials.eyebrow}</div>
            <h2 className="font-serif-display text-3xl md:text-5xl tracking-tighter max-w-3xl">
              {t.testimonials.title}
            </h2>
          </Reveal>
          <StaggerGroup className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-5">
            {testimonials.map((tm, i) => (
              <StaggerItem key={i}>
                <div className="aq-card p-8 h-full flex flex-col">
                  <Quote size={22} className="text-[#1E3A5F]" />
                  <p className="font-serif-display text-2xl leading-snug mt-4 flex-1">"{tm.quote}"</p>
                  <div className="mt-8 pt-6 border-t border-[#B6B09F]/40">
                    <div className="font-medium text-sm text-[#0A0A0A]">{tm.who}</div>
                    <div className="font-mono-aq text-[0.65rem] tracking-[0.22em] uppercase text-[#B6B09F] mt-1">{tm.role}</div>
                  </div>
                </div>
              </StaggerItem>
            ))}
          </StaggerGroup>
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="py-24 md:py-36" data-testid="pricing-section">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <Reveal className="text-center max-w-2xl mx-auto mb-16">
            <div className="label-eyebrow mb-4">— {t.pricing.eyebrow}</div>
            <h2 className="font-serif-display text-4xl md:text-6xl tracking-tighter leading-[1.05]">{t.pricing.title}</h2>
            <p className="mt-5 text-[#4A4A4A] text-lg">{t.pricing.desc}</p>
          </Reveal>

          <StaggerGroup className="grid grid-cols-1 md:grid-cols-3 gap-5" delayChildren={0.1}>
            {tiers.map((tier) => (
              <StaggerItem key={tier.id}>
                <div className={`relative p-8 md:p-10 flex flex-col h-full ${tier.featured ? "aq-card-featured text-[#EAE4D5]" : "aq-card"}`} data-testid={`pricing-card-${tier.id}`}>
                  <div className="relative">
                    <div className="flex items-baseline justify-between">
                      <h3 className="font-serif-display text-3xl">{tier.name}</h3>
                      {tier.featured && (
                        <span className="font-mono-aq text-[0.6rem] tracking-[0.25em] uppercase text-[#EAE4D5] border border-[#EAE4D5]/30 px-2.5 py-1 rounded-full">
                          {t.pricing.popular}
                        </span>
                      )}
                    </div>
                    <p className={`mt-2 text-sm ${tier.featured ? "text-[#B6B09F]" : "text-[#4A4A4A]"}`}>{tier.desc}</p>
                    <div className="mt-8 flex items-baseline gap-2">
                      <span className="font-serif-display text-5xl md:text-6xl tracking-tighter">${tier.price}</span>
                      <span className={`font-mono-aq text-xs tracking-widest ${tier.featured ? "text-[#B6B09F]" : "text-[#4A4A4A]"}`}>{t.pricing.month}</span>
                    </div>

                    <ul className="mt-8 space-y-3 flex-1">
                      {tier.features.map((f, i) => (
                        <li key={i} className="flex items-start gap-3 text-sm leading-relaxed">
                          <Check size={16} className={`mt-0.5 flex-shrink-0 ${tier.featured ? "text-[#B6B09F]" : "text-[#1E3A5F]"}`} />
                          <span className={tier.featured ? "text-[#EAE4D5]" : "text-[#0A0A0A]"}>{f}</span>
                        </li>
                      ))}
                    </ul>

                    <button
                      onClick={() => handleCheckout(tier.id)}
                      disabled={payLoading === tier.id}
                      className={`mt-10 w-full ${tier.featured ? "aq-button-ghost !bg-[#EAE4D5] !text-[#0A0A0A] !border-[#EAE4D5] hover:!bg-[#FFFFFF]" : "aq-button-primary"}`}
                      data-testid={`pricing-cta-${tier.id}`}
                    >
                      {payLoading === tier.id ? "..." : (user ? t.pricing.cta : t.pricing.cta_login)} <ArrowUpRight size={14} />
                    </button>
                  </div>
                </div>
              </StaggerItem>
            ))}
          </StaggerGroup>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 md:py-32" data-testid="faq-section">
        <div className="max-w-4xl mx-auto px-5 md:px-8">
          <Reveal className="text-center mb-12">
            <div className="label-eyebrow mb-4">— {t.faq.eyebrow}</div>
            <h2 className="font-serif-display text-4xl md:text-5xl tracking-tighter">
              {t.faq.title}
            </h2>
          </Reveal>
          <div className="aq-card overflow-hidden divide-y divide-[#B6B09F]/30">
            {faqs.map((f, i) => {
              const open = openFaq === i;
              return (
                <div key={i} data-testid={`faq-item-${i}`}>
                  <button
                    onClick={() => setOpenFaq(open ? -1 : i)}
                    className="w-full flex items-center justify-between gap-6 px-6 md:px-8 py-6 text-left hover:bg-white/40 transition-colors"
                  >
                    <span className="font-serif-display text-xl md:text-2xl pr-4">{f.q}</span>
                    <span className="w-9 h-9 rounded-full border border-[#B6B09F] flex items-center justify-center flex-shrink-0 transition-all duration-300" style={{ transform: open ? "rotate(180deg)" : "rotate(0deg)" }}>
                      {open ? <Minus size={16} /> : <Plus size={16} />}
                    </span>
                  </button>
                  <motion.div
                    initial={false}
                    animate={{ height: open ? "auto" : 0, opacity: open ? 1 : 0 }}
                    transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                    className="overflow-hidden"
                  >
                    <div className="px-6 md:px-8 pb-6 text-[#4A4A4A] leading-relaxed">{f.a}</div>
                  </motion.div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <section id="contact" className="py-24 md:py-36" data-testid="contact-section">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <div className="aq-glass-dark rounded-3xl p-10 md:p-16 text-[#EAE4D5] grid grid-cols-1 md:grid-cols-12 gap-12">
            <Reveal className="md:col-span-5">
              <div className="label-eyebrow mb-4 text-[#B6B09F]">— {t.contact.eyebrow}</div>
              <h2 className="font-serif-display text-4xl md:text-6xl tracking-tighter leading-[1.02]">{t.contact.title}</h2>
              <p className="mt-5 text-[#B6B09F] text-lg max-w-md leading-relaxed">{t.contact.desc}</p>
              <div className="mt-12 space-y-3 font-mono-aq text-sm text-[#EAE4D5]/90">
                <div>{t.contact.email_addr}</div>
                {t.contact.phone && <div>{t.contact.phone}</div>}
                <div>{t.contact.address}</div>
              </div>
            </Reveal>
            <Reveal delay={0.15} className="md:col-span-7">
              <form onSubmit={handleContact} className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="contact-form">
                <ContactInput label={t.contact.name} value={contact.name} onChange={(v) => setContact({ ...contact, name: v })} required testid="contact-name" />
                <ContactInput label={t.contact.email} type="email" value={contact.email} onChange={(v) => setContact({ ...contact, email: v })} required testid="contact-email" />
                <div className="md:col-span-2">
                  <ContactInput label={t.contact.company} value={contact.company} onChange={(v) => setContact({ ...contact, company: v })} testid="contact-company" />
                </div>
                <div className="md:col-span-2">
                  <label className="label-eyebrow text-[#B6B09F] block mb-3">{t.contact.message}</label>
                  <textarea
                    rows={5} required minLength={5}
                    value={contact.message}
                    onChange={(e) => setContact({ ...contact, message: e.target.value })}
                    className="w-full bg-transparent border-b border-[#B6B09F]/40 focus:border-[#EAE4D5] outline-none py-3 text-[#EAE4D5] text-base resize-none transition-colors"
                    data-testid="contact-message"
                  />
                </div>
                <div className="md:col-span-2 flex justify-end">
                  <button type="submit" disabled={contactLoading} className="aq-button-ghost !border-[#EAE4D5]/40 !text-[#EAE4D5] hover:!bg-[#EAE4D5] hover:!text-[#0A0A0A]" data-testid="contact-submit">
                    {contactLoading ? "..." : t.contact.submit} <ArrowUpRight size={14} />
                  </button>
                </div>
              </form>
            </Reveal>
          </div>
        </div>
      </section>

      <Footer />

      {/* ===== Hero Node Modals ===== */}
      <NodeModal
        open={modalKey === "services"}
        onClose={() => setModalKey(null)}
        eyebrow={t.services.eyebrow}
        title={t.services.title}
      >
        <p className="text-[#4A4A4A] mb-6 leading-relaxed">{t.services.desc}</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {t.services.items.map((s, i) => {
            const Icon = ICONS[s.key];
            return (
              <div key={s.key} className="aq-card p-6" data-testid={`modal-service-${s.key}`}>
                <div className="flex items-start justify-between">
                  <div className="w-10 h-10 rounded-full border border-[#B6B09F] flex items-center justify-center text-[#1E3A5F]">
                    <Icon size={18} />
                  </div>
                  <span className="font-mono-aq text-[0.65rem] tracking-[0.2em] text-[#B6B09F]">0{i + 1}</span>
                </div>
                <h3 className="font-serif-display text-2xl mt-5">{s.title}</h3>
                <p className="mt-2 text-sm text-[#4A4A4A] leading-relaxed">{s.desc}</p>
              </div>
            );
          })}
        </div>
        <button
          onClick={() => { setModalKey(null); document.getElementById("services")?.scrollIntoView({ behavior: "smooth" }); }}
          className="aq-button-primary mt-8"
          data-testid="modal-services-cta"
        >
          {t.modal.services_cta} <ArrowUpRight size={14} />
        </button>
      </NodeModal>

      <NodeModal
        open={modalKey === "pricing"}
        onClose={() => setModalKey(null)}
        eyebrow={t.pricing.eyebrow}
        title={t.pricing.title}
      >
        <p className="text-[#4A4A4A] mb-6 leading-relaxed">{t.pricing.desc}</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {tiers.map((tier) => (
            <div key={tier.id} className={`p-5 rounded-xl border ${tier.featured ? "bg-[#0A0A0A] text-[#EAE4D5] border-[#0A0A0A]" : "bg-white border-[#B6B09F]/50"}`}>
              <div className="flex items-baseline justify-between">
                <h3 className="font-serif-display text-2xl">{tier.name}</h3>
                <div className="font-serif-display text-3xl">${tier.price}</div>
              </div>
              <p className={`text-xs mt-1 ${tier.featured ? "text-[#B6B09F]" : "text-[#4A4A4A]"}`}>{tier.desc}</p>
              <button
                onClick={() => { setModalKey(null); handleCheckout(tier.id); }}
                className={`mt-5 w-full py-2 px-3 rounded-full font-mono-aq text-[0.65rem] uppercase tracking-[0.2em] ${tier.featured ? "bg-[#EAE4D5] text-[#0A0A0A]" : "bg-[#0A0A0A] text-[#EAE4D5]"}`}
                data-testid={`modal-pricing-${tier.id}`}
              >
                {user ? t.pricing.cta : t.pricing.cta_login}
              </button>
            </div>
          ))}
        </div>
      </NodeModal>

      <NodeModal
        open={modalKey === "about"}
        onClose={() => setModalKey(null)}
        eyebrow={t.about.eyebrow}
        title={t.about.title}
      >
        <p className="text-[#4A4A4A] mb-8 leading-relaxed">{t.about.desc}</p>
        <div className="grid grid-cols-2 gap-6">
          {t.about.stats.map((st, i) => (
            <div key={i} data-testid={`modal-stat-${i}`}>
              <div className="font-serif-display text-4xl md:text-5xl tracking-tighter">{st.v}</div>
              <div className="mt-2 font-mono-aq text-[0.65rem] tracking-[0.2em] uppercase text-[#0A0A0A]/60">{st.l}</div>
            </div>
          ))}
        </div>
      </NodeModal>

      <NodeModal
        open={modalKey === "contact"}
        onClose={() => setModalKey(null)}
        eyebrow={t.contact.eyebrow}
        title={t.contact.title}
      >
        <p className="text-[#4A4A4A] mb-6 leading-relaxed">{t.contact.desc}</p>
        <form onSubmit={async (e) => { await handleContact(e); setModalKey(null); }} className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="modal-contact-form">
          <ModalField label={t.contact.name} value={contact.name} onChange={(v) => setContact({ ...contact, name: v })} required testid="modal-contact-name" />
          <ModalField label={t.contact.email} type="email" value={contact.email} onChange={(v) => setContact({ ...contact, email: v })} required testid="modal-contact-email" />
          <div className="md:col-span-2">
            <ModalField label={t.contact.company} value={contact.company} onChange={(v) => setContact({ ...contact, company: v })} testid="modal-contact-company" />
          </div>
          <div className="md:col-span-2">
            <label className="label-eyebrow block mb-2">{t.contact.message}</label>
            <textarea
              rows={4} required minLength={5}
              value={contact.message}
              onChange={(e) => setContact({ ...contact, message: e.target.value })}
              className="w-full bg-white/60 border border-[#B6B09F]/50 rounded-lg px-3 py-2 text-sm focus:border-[#0A0A0A] outline-none transition-colors resize-none"
              data-testid="modal-contact-message"
            />
          </div>
          <div className="md:col-span-2 flex justify-end">
            <button type="submit" disabled={contactLoading} className="aq-button-primary" data-testid="modal-contact-submit">
              {contactLoading ? "..." : t.contact.submit} <ArrowUpRight size={14} />
            </button>
          </div>
        </form>
      </NodeModal>

      <NodeModal
        open={modalKey === "auth"}
        onClose={() => setModalKey(null)}
        eyebrow={user ? t.nav.dashboard : t.auth.register_title}
        title={user ? `${t.dashboard.welcome}, ${user.name || user.email}` : t.auth.register_title}
      >
        {user ? (
          <>
            <p className="text-[#4A4A4A] mb-6">{t.modal.auth_dashboard_pitch}</p>
            <button onClick={() => { setModalKey(null); navigate("/dashboard"); }} className="aq-button-primary" data-testid="modal-auth-dashboard">
              {t.nav.dashboard} <ArrowUpRight size={14} />
            </button>
          </>
        ) : (
          <>
            <p className="text-[#4A4A4A] mb-6">{lang === "tr" ? "Aqion'a katılın — saniyeler içinde kurulum, kredi kartı gerekmiyor." : "Join Aqion — set up in seconds, no card required."}</p>
            <div className="flex flex-wrap gap-3">
              <button onClick={() => { setModalKey(null); navigate("/register"); }} className="aq-button-primary" data-testid="modal-auth-register">
                {t.nav.register} <ArrowUpRight size={14} />
              </button>
              <button onClick={() => { setModalKey(null); navigate("/login"); }} className="aq-button-ghost" data-testid="modal-auth-login">
                {t.nav.login} <ArrowRight size={14} />
              </button>
            </div>
          </>
        )}
      </NodeModal>
    </div>
  );
}

function ModalField({ label, value, onChange, type = "text", required, testid }) {
  return (
    <div>
      <label className="label-eyebrow block mb-2">{label}</label>
      <input
        type={type} required={required}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-white/60 border border-[#B6B09F]/50 rounded-lg px-3 py-2 text-sm focus:border-[#0A0A0A] outline-none transition-colors"
        data-testid={testid}
      />
    </div>
  );
}

function ContactInput({ label, value, onChange, type = "text", required, testid }) {
  return (
    <div>
      <label className="label-eyebrow text-[#B6B09F] block mb-3">{label}</label>
      <input
        type={type} required={required}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-transparent border-b border-[#B6B09F]/40 focus:border-[#EAE4D5] outline-none py-3 text-[#EAE4D5] text-base transition-colors"
        data-testid={testid}
      />
    </div>
  );
}
